import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { addUsersMessageId } from 'modules/account/AccountService2';
import { blipDomain } from 'app.constants';
import NotificationWebLinkReact from './NotificationWebLinkReact';
import NotificationTextPlainReact from './NotificationTextPlainReact';
import './nofiticationCenterReact.scss';
const ACCEPTED_TYPES = ['text/plain', 'application/vnd.lime.web-link+json'];
const CONSUMED_KEY = 'notificationsCenter.consumedIds';
const loadConsumed = () => {
    try {
        const raw = localStorage.getItem(CONSUMED_KEY);
        if (!raw)
            return new Set();
        return new Set(JSON.parse(raw));
    }
    catch {
        return new Set();
    }
};
const saveConsumed = (consumed) => {
    try {
        localStorage.setItem(CONSUMED_KEY, JSON.stringify(Array.from(consumed)));
    }
    catch { }
};
const NotificationsCenterReact = ({ BlipService, $translate }) => {
    const [hasNotifications, setHasNotifications] = useState(false);
    // null means 'not loaded yet'; empty array means 'loaded and none available'
    const [notifications, setNotifications] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [notificationsCount, setNotificationsCount] = useState(0);
    useEffect(() => {
        let messageReceiverCleanup = null;
        let isMounted = true;
        const setupNotificationInitializer = async () => {
            if (!isMounted)
                return;
            await getNotifications();
            // Store the cleanup function returned by addMessageReceiver
            messageReceiverCleanup = BlipService.addMessageReceiver(onReceiveMessage, m => m.from.startsWith('notifications@msging.net') && m.id !== addUsersMessageId);
        };
        const initialize = async () => {
            try {
                await BlipService.waitForInitialization();
                if (isMounted) {
                    setupNotificationInitializer();
                }
            }
            catch (e) {
                console.error(e);
                if (isMounted) {
                    setHasNotifications(false);
                }
            }
        };
        initialize();
        return () => {
            isMounted = false;
            // Clean up message receiver if it was registered
            if (messageReceiverCleanup && typeof messageReceiverCleanup === 'function') {
                messageReceiverCleanup();
            }
        };
    }, []);
    const increaseNotificationsCount = () => {
        setNotificationsCount(prevCount => prevCount + 1);
    };
    const resetNotificationsCount = () => {
        setNotificationsCount(0);
    };
    const onReceiveMessage = (message) => {
        if (!ACCEPTED_TYPES.includes(message.type))
            return;
        const addReceivedStatus = n => ({ ...n, status: 'received' });
        setNotifications(prev => {
            const current = Array.isArray(prev) ? prev : [];
            if (current.find(n => message.id === n.id))
                return current;
            // if list still loading, just add message (no removal)
            if (current.length === 0)
                return [addReceivedStatus(message)];
            return [addReceivedStatus(message), ...current.slice(0, -1)];
        });
        setHasNotifications(true);
        increaseNotificationsCount();
    };
    const toggleExpandItems = (e) => {
        e.stopPropagation();
        const notificationsNumber = isExpanded ? 5 : 10;
        getNotifications(notificationsNumber);
        setIsExpanded(!isExpanded);
    };
    const getNotifications = async (take = 5) => {
        const { items: notifications } = await BlipService.sendCommand({
            method: 'get',
            to: `postmaster@threads.${blipDomain}`,
            uri: `/threads/notifications%40msging.net?direction=desc&$take=${take}`,
        });
        const uniqueNotificationsById = array => {
            let tempArray = [];
            array.forEach(n => {
                if (tempArray.length === 0 || !tempArray.find(({ id }) => id === n.id)) {
                    tempArray = tempArray.concat(n);
                }
            });
            return tempArray;
        };
        const consumedIds = loadConsumed();
        const filteredNotifications = uniqueNotificationsById(notifications)
            .filter(n => ACCEPTED_TYPES.includes(n.type) && n.id !== addUsersMessageId)
            .map(n => consumedIds.has(n.id) ? { ...n, status: 'consumed' } : n);
        // store notifications so they can be rendered (bug fix)
        setNotifications(filteredNotifications);
        const newNotifications = filteredNotifications.filter(n => n.direction === 'received' && (!n.status || n.status !== 'consumed'));
        if (newNotifications.length > 0) {
            setHasNotifications(true);
            setNotificationsCount(newNotifications.length);
        }
        else {
            setHasNotifications(false);
            resetNotificationsCount();
        }
    };
    const setOpenMenu = async () => {
        try {
            if (notifications === null) {
                await getNotifications();
            }
            const e = document.getElementById('menuNotification');
            if (e)
                e.open = !e.open;
        }
        catch (e) {
            console.error(e);
        }
    };
    const handleConsume = async (notification) => {
        if (!notification || notification.status === 'consumed')
            return;
        try {
            await BlipService.sendNotification({
                id: notification.id,
                to: 'notifications@msging.net',
                event: 'consumed',
            });
        }
        catch (e) {
            // swallow error so navigation (if any) proceeds
            console.error(e);
        }
        // persist locally
        const consumedIds = loadConsumed();
        consumedIds.add(notification.id);
        saveConsumed(consumedIds);
        // update state
        setNotifications(curr => Array.isArray(curr)
            ? curr.map(n => n.id === notification.id ? { ...n, status: 'consumed' } : n)
            : curr);
        setNotificationsCount(count => Math.max(0, count - 1));
        setHasNotifications(prev => {
            if (notificationsCount - 1 <= 0)
                return false;
            return prev;
        });
    };
    return (_jsxs("div", { className: 'notificationsCenter', children: [_jsxs("div", { className: "menu-notification", onClick: () => setOpenMenu(), children: [_jsx("bds-icon", { "data-test": "menuIconNotificationTest", id: "buttonMenuNotification", className: "round menu-icon icon-notification", class: "round menu-icon icon-notification", name: "bell", theme: "outline", "aria-label": "\u00CDcone Notifica\u00E7\u00E3o" }), hasNotifications && (_jsx("div", { className: 'absolute flex justify-center items-center notificationIndicator', children: notificationsCount }))] }), _jsx("bds-menu", { id: "menuNotification", "data-test": "menuNotificationTest", className: "menu-drop", class: "menu-drop", menu: "buttonMenuNotification", position: "right", children: Array.isArray(notifications) && notifications.length > 0 ? (_jsxs("div", { children: [_jsx("ul", { className: 'ma0 notificationsList', children: notifications.map(notification => (_jsxs("li", { className: 'bp-lh-simple cardNotification', onClick: () => handleConsume(notification), children: [notification.type === 'application/vnd.lime.web-link+json' && (_jsx(NotificationWebLinkReact, { notification: notification, "$translate": $translate, onConsume: () => handleConsume(notification) })), notification.type === 'text/plain' && (_jsx(NotificationTextPlainReact, { notification: notification, onConsume: () => handleConsume(notification) }))] }, notification.id))) }), _jsx("div", { className: "flex justify-center", children: _jsx("bds-button", { "data-test": isExpanded ? 'buttonShowLessTest' : 'buttonShowMoreTest', variant: "secondary", onClick: e => toggleExpandItems(e), size: "short", children: $translate.instant(isExpanded ? 'notificationsCenter.loadLess' : 'notificationsCenter.loadMore') }) })] })) : (_jsx("div", { className: "tc", children: _jsx("p", { className: "f4 ma0", children: $translate.instant('notificationsCenter.noNotificationsMsg') }) })) })] }));
};
export default NotificationsCenterReact;
